// /* global THREE */
import aframe from 'aframe';
import { bindEvent } from 'aframe-event-decorators';
import { randomInt } from '@micosmo/core/number';
import levels from '../levels';
import { startProcess, msWaiter } from '@micosmo/ticker/aframe-ticker';
import { afterLoadedDo } from '@micosmo/aframe/startup';

const stateNames = [
  'Loading',
  'MainMenu',
  'NewGame',
  'StartNextLevel',
  'Playing',
  'LevelComplete',
  'GameOver',
];

const controlsNames = ['Basic', 'Advanced'];

const MissileScore = 10;
const BomberScore = 50;
const SmartMissileScore = 200;

let instance = undefined;
let initialised = false;

function emitStateChange() {
  instance.el.emit('gamestatechanged', instance.data.state);
}

let silos, cities;
let citiesRemaining = 0;

let score = 0;
let highScore = 0;
let level = 0;
let multiplier = 1;

aframe.registerComponent('game-state', {
  schema: {
    state: {
      default: 'MainMenu',
      oneOf: stateNames,
    },
    controls: {
      default: 'Basic',
      oneOf: controlsNames,
    },
  },
  init() {
    if (instance !== undefined && instance !== this) {
      throw new Error('Cannot create multiple instances of game-state');
    }
    instance = this;
    this.el.sceneEl.renderer.sortObjects = true;
    this.scoreText = document.querySelector('#scoreText');
    this.multiplierText = document.querySelector('#multiplierText');
    this.levelText = document.querySelector('#levelText');
    this.alarmEl = document.querySelector('[misound__alarm]');
    this.loadingEl = document.querySelector('#LoadingInstructions');
    this.highScoresEl = document.querySelector('#highScoreTable');
    this.gamePausedEl = document.querySelector('#gamePaused');
    this.gameBoardEl = document.querySelector('#game-board');
    this.startScreenEl = document.querySelector('#startScreen');
    this.jukeboxEl = document.querySelector('[jukebox]');

    this.el.sceneEl.systems.keyboard.addListeners(this);
    this.readyCount = 0;
  },

  update(oldData) {
    if (oldData.state !== this.data.state && initialised) {
      // Wait for play start before emitting game state changes
      // Should only be ignoring 'Loading' anyway
      emitStateChange();
    }
  },

  play() {
    if (!initialised) {
      emitStateChange();
      initialised = true;
    }
  },

  keydown_Pause() {
    this.gamePausedEl.emit('startPause', {
      displayPause: true,
      endCallback: () => { }
    });
    return true;
  },
  keydown_Cursor() {
    document.getElementById('cursor').components['headless-controller'].toggleCursor();
    return true;
  },

  gamestatechanged: bindEvent(function (evt) {
    const newState = evt.detail;
    console.info(`component:game-state:gamestatechanged: ${newState}`);
    if (newState === 'Loading') {
      this.setEnvironment('env-1');
      this.multiplierText.setAttribute('visible', false);
      this.loadingEl.setAttribute('visible', true);
      this.highScoresEl.setAttribute('visible', false);
      this.gamePausedEl.setAttribute('visible', false);
      this.gameBoardEl.setAttribute('visible', true);
    } else if (newState === 'MainMenu') {
      this.jukeboxEl.setAttribute("jukebox", { state: "pause" });
      this.loadingEl.setAttribute('visible', false);
      this.setEnvironment('env-1');
      if (score > highScore) {
        highScore = score;
      }
      score = 0;
      level = 0;
      this.levelText.setAttribute('text__level', {
        value: `L${level + 1}`,
      });
      this.multiplierText.setAttribute('visible', false);
      this.highScoresEl.setAttribute('visible', true);
      this.highScoresEl.components['high-scores'].syncLatestScores();
      this.gamePausedEl.setAttribute('visible', false);
    } else if (newState === 'NewGame') {
      this.jukeboxEl.setAttribute("jukebox", { state: "on" });
      score = 0;
      this.scoreText.setAttribute('text__score', {
        value: 0,
      });
      silos = new Set(document.querySelectorAll('.missile-silo'));
      cities = new Set(document.querySelectorAll('.city'));
      citiesRemaining = cities.size;
      score = 0;
      level = 0;
      multiplier = levels[Math.min(level, levels.length - 1)].multiplier || 1;
      this.levelText.setAttribute('text__level', {
        value: `L${level + 1}`,
      });
      this.el.removeAttribute('level');

      const env =
        levels[Math.min(level, levels.length - 1)].environment || 'env-1';
      this.setEnvironment(env);

      this.alarmEl.components.misound__alarm.playSound();
      setTimeout(() => this.el.setAttribute('game-state', 'state', 'Playing'), 2100);
      this.multiplierText.setAttribute('visible', false);
      this.highScoresEl.setAttribute('visible', false);
    } else if (newState === 'StartNextLevel') {
      this.levelText.setAttribute('text__level', {
        value: `L${level + 1}`,
      });

      console.log(`LEVEL: ${level + 1}`);

      const env =
        levels[Math.min(level, levels.length - 1)].environment || 'env-1';
      this.setEnvironment(env);

      multiplier = levels[Math.min(level, levels.length - 1)].multiplier || 1;

      if (multiplier !== 1) {
        this.multiplierText.setAttribute(
          'text__multiplier',
          'value',
          `${multiplier}X      POINTS`
        );
        this.multiplierText.setAttribute('visible', true);
      }

      this.alarmEl.components.misound__alarm.playSound();
      setTimeout(() => this.el.setAttribute('game-state', 'state', 'Playing'), 2100);
    } else if (newState === 'Playing') {
      this.multiplierText.setAttribute('visible', false);

      const levelData = levels[Math.min(level, levels.length - 1)];
      this.el.setAttribute('level', {
        duration: levelData.duration,
        waveSize: levelData.waveSize,
        generatorConfig: JSON.stringify(levelData.generators),
      });
      this.el.emit('newLevelPlaying');
    } else if (newState === 'LevelComplete') {
      this.el.removeAttribute('level');
      if (citiesRemaining === 0) {
        this.el.setAttribute('game-state', 'state', 'GameOver');
      }
      level += 1;
    } else if (newState === 'GameOver') {
      this.el.removeAttribute('level');
      this.highScoresEl.setAttribute('visible', true);
      this.highScoresEl.components['high-scores'].syncLatestScores();
    }

    this.scoreText.setAttribute(
      'visible',
      newState !== 'MainMenu' && newState !== 'Loading'
    );
    this.levelText.setAttribute(
      'visible',
      newState !== 'MainMenu' && newState !== 'Loading'
    );
  }),
  missiledestroyed: bindEvent(function (evt) {
    const missile = evt.detail;
    if (!missile.data.isPlayer) {
      this.el.emit('addscore', MissileScore);
    }
  }),
  bomberdestroyed: bindEvent(function (evt) {
    this.el.emit('addscore', BomberScore);
  }),
  smartmissiledestroyed: bindEvent(function (evt) {
    this.el.emit('addscore', SmartMissileScore);
  }),
  addscore: bindEvent(function (evt) {
    const amount = evt.detail * multiplier;
    score += amount;
    this.scoreText.setAttribute('text__score', {
      value: score,
    });
  }),
  citydestroyed: bindEvent(function () {
    citiesRemaining = Array.from(cities).filter(c => !c.components.city.isDead).length;
    if (citiesRemaining === 0)
      startProcess(msWaiter(2000, () => this.el.setAttribute('game-state', 'state', 'GameOver')));
  }),
  actorready: bindEvent(function () {
    this.readyCount += 1;
    if (this.readyCount >= 10 && !this.gameLoaded) {
      this.gameLoaded = true;
      afterLoadedDo(() => this.el.setAttribute('game-state', 'state', 'MainMenu'));
    }
  }),
  getRandomSiloOrCity() {
    const n = silos.size + cities.size;
    let r = randomInt(0, n - 1);
    let fromSet = silos;
    if (r >= silos.size) {
      fromSet = cities;
      r -= silos.size;
    }
    let i = 0;
    for (const x of fromSet) {
      if (i === r) {
        return x;
      }
      i += 1;
    }
  },
  nearestLiveSiloOrCityToPoint(p) {
    let nearest, distance;
    for (const silo of silos) {
      if (!silo.object3D.visible) continue;
      const d = silo.object3D.position.distanceToSquared(p);
      if (!nearest || d < distance) {
        nearest = silo;
        distance = d;
      }
    }
    for (const city of cities) {
      if (!city.object3D.visible) continue;
      const d = city.object3D.position.distanceToSquared(p);
      if (!nearest || d < distance) {
        nearest = city;
        distance = d;
      }
    }
    return nearest;
  },
  removeDead(set) {
    for (const x of set) {
      if (x.getAttribute('visible') === false) {
        set.delete(x);
      }
    }
  },
  isAdvancedControls() {
    return this.data.controls === 'Advanced';
  },
  isBasicControls() {
    return this.data.controls === 'Basic';
  },
  setEnvironment: (() => {
    let currentEnvironment;
    return function (id) {
      if (id === currentEnvironment) {
        return;
      }
      Array.from(
        document.querySelector('#environments').children
      ).forEach(e => e.setAttribute('visible', e.id === id));
      currentEnvironment = id;
    };
  })(),
});

window.newgame = () => {
  document.querySelector('[game-state]').setAttribute('game-state', {
    state: 'NewGame',
  });

  document
    .querySelector('[explosion-spawner]')
    .setAttribute('explosion-spawner', 'enabled', true);
};

export default {
  get instance() {
    return instance;
  },
  get score() {
    return score;
  },
  get highScore() {
    return highScore;
  },
  get multiplier() {
    return multiplier;
  },
};
